import { Radio } from '@iwoca/orion';
import styles from './OfferChoiceButtons.module.css';
import { FormattedMessage } from 'react-intl';
import {
    getNonInterestOnlyPaymentAmount,
    isInterestOnly
} from '../../../../myOffers/MyOffers.helpers';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { getMonthsfromDays } from '../../../../multiLoanDashboard/accountsSelector';
import {
    GetDrawDownRepaymentScheduleCreditFacilityResponse,
    GetFormalOffersResponse
} from '@iwoca/lapi-client/edge';
import durationMessages from './durationChoice.messages';
import interestOnlyMessages from '../interestOnly/InterestOnly.messages';
import { getCountry } from '@iwIntlProvider/CountryProvider';

export const OfferChoiceButton = ({
    offer,
    updateSelectedOffer,
    drawdownAmount
}: {
    offer: NonNullable<GetFormalOffersResponse['data']>['formal_offers'][0];
    updateSelectedOffer: (
        offer: NonNullable<GetFormalOffersResponse['data']>['formal_offers'][0]
    ) => void;
    drawdownAmount: number;
}) => {
    const stateKey = useStateKey();

    const { data: minRepaymentAmount } = useQuery({
        queryKey: [
            'draw_down_repayment_schedule',
            stateKey,
            drawdownAmount,
            offer.offer_id,
            offer.product_type
        ],
        queryFn: async () => {
            // eslint-disable-next-line pandora/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/draw_down_repayment_schedule/${stateKey}/${offer.product_type}/${offer.offer_id}/?amount=${drawdownAmount}`
            );

            return response.json();
        },
        select: (data: GetDrawDownRepaymentScheduleCreditFacilityResponse) =>
            //equal repayments does not exist for interest-only offers
            data.data.summary.simplified_schedule?.equal_repayments
                ? data.data.summary.simplified_schedule?.equal_repayments.amount
                : getCountry() === 'DE' // use first non-interest only repayment for DE
                  ? getNonInterestOnlyPaymentAmount(data.data.schedule)
                  : data.data.summary.simplified_schedule?.next_repayment
                        ?.amount
    });

    const getLabel = (
        offer: NonNullable<GetFormalOffersResponse['data']>['formal_offers'][0]
    ): React.ReactNode => {
        if (isInterestOnly(offer)) {
            return (
                <div className={styles.radioButtonLabel}>
                    <FormattedMessage
                        //@ts-ignore
                        {...interestOnlyMessages.interestOnly}
                    />
                    <span className={styles.radioButtonSubtext}>
                        {minRepaymentAmount && (
                            <FormattedMessage
                                //@ts-ignore
                                {...interestOnlyMessages.interestOnlyRepaymentAmount}
                                values={{
                                    amount: (
                                        <FormattedCurrency
                                            value={minRepaymentAmount}
                                        />
                                    )
                                }}
                            />
                        )}
                    </span>
                </div>
            );
        } else {
            const loanDuration =
                offer.duration!.unit === 'days'
                    ? getMonthsfromDays(offer.duration!.amount)
                    : offer.duration!.amount;

            return (
                <div className={styles.radioButtonLabel}>
                    <FormattedMessage
                        {...durationMessages.monthlyRepaymentsDuration}
                        values={{
                            months: loanDuration
                        }}
                    />

                    {minRepaymentAmount && (
                        <span className={styles.radioButtonSubtext}>
                            <FormattedMessage
                                {...durationMessages.repaymentAmount}
                                values={{
                                    amount: (
                                        <FormattedCurrency
                                            value={minRepaymentAmount}
                                        />
                                    )
                                }}
                            />
                        </span>
                    )}
                </div>
            );
        }
    };

    return (
        <Radio
            key={offer.offer_id}
            frame
            id={offer.offer_id}
            label={getLabel(offer) as string}
            name="choice-buttons"
            onChange={() => {
                updateSelectedOffer(offer);
            }}
            className={styles.firstRadioButton}
            disabled={drawdownAmount > offer.available_cash!}
        />
    );
};
